import React from 'react';
import type { ScriptFormData } from '../types';
import { VideoType } from '../types';
import { VIDEO_TYPE_OPTIONS, VIDEO_STYLE_OPTIONS, CTA_OPTIONS } from '../constants';
import { GenerateIcon } from './icons/GenerateIcon';

interface ScriptFormProps {
  formData: ScriptFormData;
  setFormData: React.Dispatch<React.SetStateAction<ScriptFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onClear?: () => void;
  isLoading: boolean;
}

const FormInput: React.FC<{ label: string; id: keyof ScriptFormData; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; placeholder: string, required?: boolean }> = ({ label, id, value, onChange, placeholder, required = true }) => (
  <div>
    <label htmlFor={id} className="block mb-1 text-[10px] font-black text-[#3db8c5] uppercase tracking-widest">
      {label}
    </label>
    <input
      type="text"
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="bg-[#002d5b]/40 text-white border-2 border-[#3db8c5]/40 text-sm rounded-md focus:ring-0 focus:border-[#f15a24] block w-full p-3 placeholder-gray-500 font-bold transition-all duration-200 input-focus-glow"
      required={required}
    />
  </div>
);

const FormTextarea: React.FC<{ label: string; id: keyof ScriptFormData; value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; placeholder: string, required?: boolean }> = ({ label, id, value, onChange, placeholder, required = true }) => (
  <div>
    <label htmlFor={id} className="block mb-1 text-[10px] font-black text-[#3db8c5] uppercase tracking-widest">
      {label}
    </label>
    <textarea
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={4}
      className="bg-[#002d5b]/40 text-white border-2 border-[#3db8c5]/40 text-sm rounded-md focus:ring-0 focus:border-[#f15a24] block w-full p-3 placeholder-gray-500 font-bold transition-all duration-200 input-focus-glow"
      required={required}
    />
  </div>
);


export const ScriptForm: React.FC<ScriptFormProps> = ({ formData, setFormData, onSubmit, onClear, isLoading }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVideoTypeChange = (newType: VideoType) => {
    setFormData(prev => ({
      ...prev,
      videoType: newType,
      // Clear mode-specific fields when switching
      make: newType === VideoType.Trending ? '' : prev.make,
      model: newType === VideoType.Trending ? '' : prev.model,
      year: newType === VideoType.Trending ? '' : prev.year,
      keyFeatures: '', // Always clear - different purpose per mode
    }));
  };


  return (
    <div className="relative h-full group">
      {/* Shadow Layer - SCP Teal */}
      <div className="absolute top-2 left-2 w-full h-full bg-[#3db8c5] z-0"></div>

      {/* Main Container - SCP Navy BG, Orange Accents, 5px White Outline */}
      <div className="relative z-10 bg-[#002d5b] border-2 border-[#f15a24] outline outline-[5px] outline-white p-6 lg:p-8 h-full">

        <div className="flex items-center justify-start mb-8 border-b-2 border-[#f15a24]/30 pb-4">
          <div className="w-3 h-8 bg-[#f15a24] mr-3 skew-x-[-12deg]"></div>
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter">
            Mission Briefing
          </h2>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label className="block mb-2 text-[10px] font-black text-[#3db8c5] uppercase tracking-widest">Operation Type</label>
            <div className="grid grid-cols-2 gap-3 bg-black/20 p-2 rounded-lg border-2 border-[#3db8c5]/30">
              {VIDEO_TYPE_OPTIONS.map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleVideoTypeChange(type)}
                  className={`w-full py-3 text-xs font-black uppercase rounded-md transition-all duration-200 focus:outline-none border-2 ${formData.videoType === type
                    ? 'bg-[#f15a24] text-white border-[#f15a24] shadow-[0_0_15px_rgba(241,90,36,0.4)]'
                    : 'bg-transparent text-white/40 border-transparent hover:text-white hover:bg-white/5'
                    }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {formData.videoType === VideoType.Vehicle ? (
            <div className="space-y-5 animate-fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormInput label="Car Make" id="make" value={formData.make} onChange={handleChange} placeholder="Make" required={formData.videoType === VideoType.Vehicle} />
                <FormInput label="Car Model" id="model" value={formData.model} onChange={handleChange} placeholder="Model" required={formData.videoType === VideoType.Vehicle} />
              </div>
              <FormInput label="Year" id="year" value={formData.year} onChange={handleChange} placeholder="Year" required={formData.videoType === VideoType.Vehicle} />
              <FormTextarea label="Key Features" id="keyFeatures" value={formData.keyFeatures} onChange={handleChange} placeholder="Key features... (recommended 3 features max)" required={formData.videoType === VideoType.Vehicle} />
            </div>
          ) : (
            <div className="space-y-5 animate-fade-in">
              <div className="bg-gradient-to-r from-[#f15a24]/10 to-transparent border-l-4 border-[#f15a24] p-4 rounded-r-lg mb-4">
                <p className="text-white/80 text-sm font-medium">
                  🎯 <span className="text-[#f15a24] font-bold">Trending Mode:</span> Describe what kind of content you want. Gemini will find a viral trend that fits.
                </p>
              </div>
              <FormTextarea 
                label="Topic / Concept" 
                id="keyFeatures" 
                value={formData.keyFeatures} 
                onChange={handleChange} 
                placeholder="What's the vibe? Examples:
• 'Something funny about waiting for customers'
• 'Show off our new inventory drop'
• 'Day in the life of a car salesperson'
• 'Roast my own dealership'" 
                required={false} 
              />
            </div>
          )}

          <FormInput label="Target Audience" id="targetAudience" value={formData.targetAudience} onChange={handleChange} placeholder="Target audience..." />

          <div>
            <label htmlFor="videoStyle" className="block mb-1 text-[10px] font-black text-[#3db8c5] uppercase tracking-widest">
              Video Style
            </label>
            <select
              id="videoStyle"
              name="videoStyle"
              value={formData.videoStyle}
              onChange={handleChange}
              className="bg-[#002d5b]/40 text-white border-2 border-[#3db8c5]/40 text-sm rounded-md focus:ring-0 focus:border-[#f15a24] focus:shadow-[0_0_10px_rgba(241,90,36,0.3)] block w-full p-3 font-bold transition-all duration-200"
            >
              {VIDEO_STYLE_OPTIONS.map(style => (
                <option key={style} value={style}>{style}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="cta" className="block mb-1 text-[10px] font-black text-[#3db8c5] uppercase tracking-widest">
              Call to Action
            </label>
            <select
              id="cta"
              name="cta"
              value={formData.cta}
              onChange={handleChange}
              className="bg-[#002d5b]/40 text-white border-2 border-[#3db8c5]/40 text-sm rounded-md focus:ring-0 focus:border-[#f15a24] focus:shadow-[0_0_10px_rgba(241,90,36,0.3)] block w-full p-3 font-bold transition-all duration-200"
            >
              {CTA_OPTIONS.map(cta => (
                <option key={cta} value={cta}>{cta}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 mt-6">
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="px-6 py-4 inline-flex items-center justify-center text-[#3db8c5] bg-transparent hover:bg-[#3db8c5]/10 focus:ring-0 focus:outline-none font-black uppercase tracking-wide rounded-lg text-sm text-center transition-all duration-200 border-2 border-[#3db8c5]/50 hover:border-[#3db8c5]"
              >
                Clear
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 inline-flex items-center justify-center text-white bg-[#f15a24] hover:bg-[#d44d1b] focus:ring-0 focus:outline-none font-black uppercase tracking-wide rounded-lg text-lg px-6 py-4 text-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_0_#9a3412] active:shadow-none active:translate-y-[4px] border-2 border-[#f15a24] hover:border-[#d44d1b] btn-hover-scale"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  PROCESSING...
                </>
              ) : (
                <>
                  <GenerateIcon className="mr-3 w-6 h-6" />
                  INITIATE SCRIPT
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};